/**
 * The model of the fournisseur portal « Réclamations reçues » (/portail-fournisseur/reclamations and
 * /:id — design/ux/03 §5.1 « Fournisseur sees », §5.8, §5.9; refonte WP-36). Pure functions, unit
 * tested: the pages own no rule. The server stays the authority — it scopes every read to the
 * principal's org, filters, counts, lists the actions open to the supplier (`actions`, the D-20
 * transition table with the side, the channel and the live link already applied) and refuses anything
 * else; this module only reads those answers so a screen never offers an action that would be refused.
 * What both planes share is read from `lib/reclamation-journal.js` (one envelope reader, one line
 * model, one state vocabulary) — never a second copy.
 *
 *   · query      `paramsReclamationsRecues`, `vuePortailValide`, `lireReclamationsRecues`
 *   · views      `ongletsVuesPortail`, cells `titreLignePortail`, the menu badge `badgeReclamations`
 *   · rights     `droitsPortail`, `actionsPortail`
 *   · document   `phraseStatutPortail`, `bandeauPropositionPortail`, `filPortail`, `lienNoteCredit`
 *   · « Proposer un avoir »  `etatProposition`, `valeurAccordeeCentimes`, `totalProposition`,
 *                `erreursProposition`, `corpsProposition`
 */
import {
  ACTION_RECLAMATION as A,
  COTE_RECLAMATION,
  ETAT_PROPOSITION_RECLAMATION,
  RECLAMATION_BORNES as B,
  STATUT_RECLAMATION as S,
  TYPE_EVENEMENT_RECLAMATION as T,
} from "@pharmakon/shared/constants/reclamations.js";
import { valeurLigneAvoirCentimes } from "@pharmakon/shared/pricing/avoir.js";
import { versCentimes, versTexteDecimal } from "@pharmakon/shared/pricing/money.js";
import { supplierCan } from "@/config/fournisseur.constants.js";
import { fmtDate, fmtMoney, fmtTime } from "@/config/locale.config.js";
import { paramsJournal } from "@/lib/achat-journal.js";
import { lireJournalReclamations, nomProduit } from "@/lib/reclamation-journal.js";
import { ORDRE_VUES_PORTAIL, PARAMS_NOTE_CREDIT, URL_AVOIRS_PORTAIL, VUE_DEFAUT_PORTAIL } from "@/config/reclamation.config.js";
import { M_RP, M_RPD, M_RPDLG, M_RPFIL_EVENEMENTS } from "@/config/reclamation-portail.messages.js";

const da = (v) => fmtMoney(v, { noSuffix: true });
const dateHeure = (d) => (d ? `${fmtDate(d)} ${fmtTime(d)}` : "");
const pharmacieDe = (doc) => doc?.pharmacie?.nom ?? "La pharmacie";

// ─── Query and payload ───────────────────────────────────────────────────────────────────────────

/** The view of the URL, or the default one (an unknown value never reaches the server). */
export const vuePortailValide = (v) => (ORDRE_VUES_PORTAIL.includes(v) ? v : VUE_DEFAUT_PORTAIL);

/**
 * The `GET /fournisseur-portail/reclamations` query: the filter bar's values (lists comma-joined, empty
 * values dropped), the view, the sort and the page. The view narrows the rows; the view counts stay
 * those of the other filters (server rule).
 * @param {Record<string, unknown>} queryParams `useFilters().queryParams`
 * @param {{ vue?: string, sort?: { by: string, order: string } | null, page?: number, limit?: number }} o
 */
export function paramsReclamationsRecues(queryParams, { vue, sort = null, page, limit } = {}) {
  const out = paramsJournal(queryParams, { onglet: undefined, sort, page, limit });
  delete out.onglet;
  if (page == null) delete out.page;
  if (limit == null) delete out.limit;
  out.vue = vuePortailValide(vue);
  return out;
}

/** The list page from the server envelope — the journal's reader (same envelope on both planes). */
export const lireReclamationsRecues = lireJournalReclamations;

/** The view tabs with the server counts; none while they are not trustworthy. */
export function ongletsVuesPortail(compteurs, fiables) {
  return ORDRE_VUES_PORTAIL.map((v) => ({
    value: v,
    label: M_RP.vues[v],
    count: fiables && compteurs ? compteurs[v] ?? 0 : undefined,
  }));
}

/** The row tooltip at 1024 (facture and activity leave the grid, ux/03 §5.8). */
export const titreLignePortail = (r) => M_RP.ligneTitre({ facture: r?.facture?.libelle ?? null, activite: r?.activite ? fmtDate(r.activite) : null });

/**
 * The menu badge (A-20 « unread + awaiting action »): the server's `total`, each réclamation counted
 * once; 0 for anything unreadable (a failed read never shows a wrong number).
 */
export function badgeReclamations(body) {
  const d = body?.data ?? body;
  const n = Number(d?.total);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

// ─── Rights and actions ──────────────────────────────────────────────────────────────────────────

/** The supplier's rights (UI gating; the server enforces — OP-03-31). */
export function droitsPortail(role) {
  return {
    lire: supplierCan(role, "fournisseur_commercial.read"),
    agir: supplierCan(role, "fournisseur_commercial.manage"),
    // « Émettre une note de crédit » opens the finance wizard — its own right
    noteCredit: supplierCan(role, "fournisseur_compta.manage"),
  };
}

/**
 * The actions a réclamation offers to this supplier user: the server's `actions` (D-20, its side and
 * channel, the live link) AND the role's right to act. `lectureSeule` when the server would let the
 * org act but this role may only read (the page says so instead of showing dead buttons).
 */
export function actionsPortail(doc, droits) {
  const ouvertes = new Set(doc?.actions ?? []);
  const peut = (a) => ouvertes.has(a) && Boolean(droits?.agir);
  return {
    prendreEnCharge: peut(A.PRENDRE_EN_CHARGE),
    accepter: peut(A.ACCEPTER),
    proposer: peut(A.PROPOSER),
    rejeter: peut(A.REJETER),
    message: peut(A.MESSAGE),
    pieceJointe: peut(A.PIECE_JOINTE),
    lectureSeule: ouvertes.size > 0 && !droits?.agir,
  };
}

// ─── Document (§5.8) ─────────────────────────────────────────────────────────────────────────────

/** ux/03 §5.1 « Fournisseur (connected) sees » — the sentence of the state band. */
export function phraseStatutPortail(doc) {
  if (!doc) return "";
  switch (doc.statut) {
    case S.ENVOYEE:
      return M_RPD.statut.ENVOYEE(fmtDate(doc.envoyee_at));
    case S.EN_COURS:
      return M_RPD.statut.EN_COURS(fmtDate(doc.prise_en_charge_at));
    case S.RESOLUE:
      return M_RPD.statut.RESOLUE(fmtDate(doc.resolue_at), doc.resolution ?? "—");
    case S.REJETEE:
      return M_RPD.statut.REJETEE(fmtDate(doc.rejetee_at), doc.motif_rejet ?? "—");
    case S.CLOTUREE:
      return doc.motif_retrait ? M_RPD.statut.RETIREE(fmtDate(doc.cloturee_at), doc.motif_retrait) : M_RPD.statut.CLOTUREE(fmtDate(doc.cloturee_at));
    default:
      return "";
  }
}

/**
 * The band of the supplier's own proposition: pending (« … attend la réponse de {pharmacie} »), or
 * refused (the pharmacy's answer is in the thread; a new one can be made). None otherwise.
 * @returns {{ ton: "info"|"warning", texte: string } | null}
 */
export function bandeauPropositionPortail(doc) {
  const p = doc?.proposition;
  if (!p) return null;
  if (p.etat === ETAT_PROPOSITION_RECLAMATION.EN_ATTENTE) {
    const lignes = (p.lignes ?? []).filter((l) => Number(l.quantite) > 0).length;
    return { ton: "info", texte: M_RPD.propositionEnAttente({ pharmacie: pharmacieDe(doc), montant: p.montant ? da(p.montant) : null, lignes }) };
  }
  if (p.etat === ETAT_PROPOSITION_RECLAMATION.REFUSEE && [S.EN_COURS, S.ENVOYEE].includes(doc.statut)) {
    return { ton: "warning", texte: M_RPD.propositionRefusee(pharmacieDe(doc)) };
  }
  return null;
}

/** The words of one timeline event, from the supplier's side (« Vous avez pris en charge … »). */
function texteEvenementPortail(e, doc) {
  const pharmacie = pharmacieDe(doc);
  const vous = e.cote === COTE_RECLAMATION.FOURNISSEUR;
  const W = M_RPFIL_EVENEMENTS;
  switch (e.type) {
    case T.CREATION:
      return W.CREATION({ pharmacie });
    case T.ENVOI:
      return W.ENVOI({ pharmacie });
    case T.RELANCE:
      return W.RELANCE({ pharmacie });
    case T.PRISE_EN_CHARGE:
      return W.PRISE_EN_CHARGE({ auteur: e.auteur });
    case T.MESSAGE:
      return vous ? W.MESSAGE_VOUS({ auteur: e.auteur }) : W.MESSAGE_PHARMACIE({ pharmacie });
    case T.PIECE_JOINTE:
      return W.PIECE_JOINTE({ n: (e.payload?.pieces ?? []).length || 1, vous, pharmacie });
    case T.PROPOSITION:
      return W.PROPOSITION({ montant: e.payload?.montant ? da(e.payload.montant) : null });
    case T.PROPOSITION_ACCEPTEE:
      return W.PROPOSITION_ACCEPTEE({ pharmacie });
    case T.PROPOSITION_REFUSEE:
      return W.PROPOSITION_REFUSEE({ pharmacie });
    case T.RESOLUTION:
      return W.RESOLUTION();
    case T.REJET:
      return W.REJET();
    case T.AVOIR_GENERE:
      return W.AVOIR_GENERE({ pharmacie, numero: e.payload?.numero_avoir ?? null, montant: e.payload?.montant_ttc ? da(e.payload.montant_ttc) : null });
    case T.CLOTURE:
      return W.CLOTURE({ pharmacie });
    case T.RETRAIT:
      return W.RETRAIT({ pharmacie });
    default:
      return W.AUTRE();
  }
}

/**
 * The thread as the supplier reads it (oldest first, the server's order — ONE thread with the
 * pharmacy plane, its internal events already removed by the server). Same item shape as the
 * pharmacy's `filDocument`, so the one timeline component renders both planes.
 */
export function filPortail(doc) {
  return (doc?.evenements ?? []).map((e) => ({
    id: e.id,
    quand: dateHeure(e.date),
    texte: texteEvenementPortail(e, doc),
    message: e.message ?? null,
    cote: e.cote,
    fournisseur: e.cote === COTE_RECLAMATION.FOURNISSEUR,
    interne: false,
    echec: false,
    pieces: e.type === T.PIECE_JOINTE ? (e.payload?.pieces ?? []) : [],
    avoirId: null,
  }));
}

/** The facture line of the document: « A18/110129 du 11/08/2026 ». */
export const libelleFacturePortail = (f) => (f ? M_RPD.facture({ libelle: f.libelle, date: f.date_facture ? fmtDate(f.date_facture) : null }) : null);

/**
 * ux/03 §5.8 « Émettre une note de crédit sur l'e-facture {EF-…} »: once resolved (Résolue, or
 * Clôturée after a resolution), anchored to this org's own e-facture, for a role that issues credit
 * notes — the finance « Nouvel avoir » wizard pre-filled with the e-facture, the granted amount and
 * the réclamation's reference. DECISIONS A-63 (6). Null otherwise.
 */
export function lienNoteCredit(doc, droits) {
  if (!droits?.noteCredit || !doc?.e_facture?.id || !doc.resolue_at) return null;
  if (![S.RESOLUE, S.CLOTUREE].includes(doc.statut)) return null;
  const p = new URLSearchParams();
  p.set(PARAMS_NOTE_CREDIT.OUVRIR, "1");
  p.set(PARAMS_NOTE_CREDIT.E_FACTURE, doc.e_facture.id);
  if (doc.montant_accorde) p.set(PARAMS_NOTE_CREDIT.MONTANT, doc.montant_accorde);
  p.set(PARAMS_NOTE_CREDIT.MOTIF, `Réclamation ${doc.numero ?? ""} — ${doc.objet ?? ""}`.slice(0, B.motif_max));
  return { to: `${URL_AVOIRS_PORTAIL}?${p.toString()}`, libelle: M_RPD.noteCredit(doc.e_facture.numero ?? "") };
}

// ─── « Proposer un avoir… » (§5.8) ───────────────────────────────────────────────────────────────

/**
 * The dialog's state: one row per claimed line with « Qté accordée » defaulting to the claimed
 * quantity; « Montant accordé » (default = the claimed amount) only when no line carries a price — a
 * priced line's value is the avoir arithmetic of the server, never typed.
 */
export function etatProposition(doc) {
  const lignes = (doc?.lignes ?? []).map((l) => ({
    id: l.id,
    produit: nomProduit(l.produit),
    lot: l.numero_lot ?? "—",
    reclamee: Number(l.quantite_reclamee) || 0,
    accordee: String(Number(l.quantite_reclamee) || 0),
    prix_ht: l.prix_ht ?? null,
    taux_tva: l.taux_tva ?? null,
  }));
  const prix = lignes.some((l) => l.prix_ht != null);
  return { lignes, prix, montant: prix ? "" : String(doc?.montant_reclame ?? ""), message: "" };
}

/** A granted quantity as typed: an integer 0 … claimed, else null. */
export function quantiteAccordee(texte, max) {
  const t = String(texte ?? "").trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 0 && n <= max ? n : null;
}

/** The value of a granted line (qté × PU net HT + TVA, the avoir's F4-F5) in centimes; null without a price. */
export function valeurAccordeeCentimes(l) {
  const q = quantiteAccordee(l.accordee, l.reclamee);
  if (q == null || l.prix_ht == null) return null;
  return valeurLigneAvoirCentimes({ quantite: q, pu_net_ht: l.prix_ht, taux_tva: l.taux_tva ?? 0 }).ttc;
}

const MONTANT = /^\d{1,10}([.,]\d{1,2})?$/;

/** The proposal's total: Σ priced lines, else the typed amount. `{ centimes, texte } | null`. */
export function totalProposition(etat) {
  if (etat.prix) {
    const valeurs = etat.lignes.map(valeurAccordeeCentimes).filter((v) => v != null);
    if (!valeurs.length) return null;
    const c = valeurs.reduce((s, v) => s + v, 0);
    return { centimes: c, texte: versTexteDecimal(c) };
  }
  const t = etat.montant.trim();
  if (!t || !MONTANT.test(t)) return null;
  const c = versCentimes(t.replace(",", "."));
  return { centimes: c, texte: versTexteDecimal(c) };
}

/**
 * The dialog's refusals before the request (the server repeats them): per line, the amount, the
 * message, and « nothing granted ». Empty object = sendable.
 * @param {ReturnType<typeof etatProposition>} etat
 * @param {{ montant_reclame?: string|null }} doc
 */
export function erreursProposition(etat, doc) {
  const E = M_RPDLG.erreurs;
  const out = {};
  const lignes = {};
  for (const l of etat.lignes) if (quantiteAccordee(l.accordee, l.reclamee) == null) lignes[l.id] = E.quantite(l.reclamee);
  if (Object.keys(lignes).length) out.lignes = lignes;
  if (!etat.prix) {
    const t = etat.montant.trim();
    if (t && !MONTANT.test(t)) out.montant = E.montant;
    else if (t) {
      const c = versCentimes(t.replace(",", "."));
      if (c <= 0) out.montant = E.montant;
      else if (doc?.montant_reclame != null && c > versCentimes(doc.montant_reclame)) out.montant = E.montantTrop(da(doc.montant_reclame));
    }
  }
  if (!out.lignes && !out.montant) {
    const t = totalProposition(etat);
    const qte = etat.lignes.some((l) => (quantiteAccordee(l.accordee, l.reclamee) ?? 0) > 0);
    if ((!t || t.centimes <= 0) && !qte) out.rien = E.rien;
  }
  const m = etat.message.trim();
  if (m.length < B.message_min) out.message = E.message;
  return out;
}

/** The `POST …/proposer` body: the granted quantity of each line, the amount when no line is priced. */
export function corpsProposition(etat) {
  const t = etat.prix ? null : totalProposition(etat);
  return {
    lignes: etat.lignes.map((l) => ({ ligne_reclamation_id: l.id, quantite: quantiteAccordee(l.accordee, l.reclamee) ?? 0 })),
    montant: t ? t.texte : null,
    message: etat.message.trim(),
  };
}
